import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminHeader, Drawer } from '@/features/admin/ui';
import { useRequireRole } from '@/lib/auth/useRequireRole';
import {
  catalogMutations,
  catalogQueries,
  nextSortOrder,
  reorderSwap,
  type NavMenuItem,
} from '@/lib/queries/catalog';

export const Route = createFileRoute('/admin/menu')({
  component: MenuPage,
});

function MenuPage() {
  useRequireRole(['admin']); // admin-only; moderators 403 on direct nav (Spec 21)
  const qc = useQueryClient();
  const list = useQuery(catalogQueries.menuItems());
  const rows = [...(list.data ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const [editing, setEditing] = useState<Partial<NavMenuItem> | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['catalog', 'menu'] });

  const reorder = useMutation({
    mutationFn: async ({ row, dir }: { row: NavMenuItem; dir: -1 | 1 }) => {
      const swap = reorderSwap(rows, row.id, dir);
      if (!swap) return;
      await catalogMutations.reorderMenuItems(swap);
    },
    onSuccess: invalidate,
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Reorder failed.'),
  });

  const toggleVisible = useMutation({
    mutationFn: async (row: NavMenuItem) =>
      catalogMutations.saveMenuItem({
        id: row.id,
        label: row.label,
        href: row.href,
        is_visible: !row.is_visible,
        sort_order: row.sort_order,
      }),
    onSuccess: invalidate,
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Update failed.'),
  });

  const remove = useMutation({
    mutationFn: async (row: NavMenuItem) => catalogMutations.deleteMenuItem(row.id),
    onSuccess: () => {
      toast.success('Menu item deleted.');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Delete failed.'),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <AdminHeader kicker="Admin · Browse" title="Menu" />
      <p className="mt-4 font-sans text-sm text-ink/60">
        The horizontal navigation bar under the top bar of the streaming browse page. Items render
        left to right in this order; hidden items stay configured but don&apos;t appear.
      </p>

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={() => setEditing({ is_visible: true, sort_order: nextSortOrder(rows) })}
          className="rounded-md border border-ink/20 px-3 py-1.5 font-sans text-xs font-medium uppercase tracking-[0.12em] text-ink hover:border-oxblood hover:text-oxblood"
        >
          Add item
        </button>
      </div>

      {list.isLoading ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">
          No menu items yet. Add one to build the navigation bar.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-ink/10">
          {rows.map((r, i) => (
            <li key={r.id} className="flex items-center gap-3 py-3">
              <span className="flex flex-col">
                <button
                  type="button"
                  disabled={i === 0}
                  onClick={() => reorder.mutate({ row: r, dir: -1 })}
                  className="text-ink/40 hover:text-oxblood disabled:opacity-20"
                  aria-label="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled={i === rows.length - 1}
                  onClick={() => reorder.mutate({ row: r, dir: 1 })}
                  className="text-ink/40 hover:text-oxblood disabled:opacity-20"
                  aria-label="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </span>
              <button type="button" onClick={() => setEditing(r)} className="flex-1 text-left">
                <span className="font-sans text-base text-ink">{r.label}</span>
                <span className="ml-2 font-sans text-xs text-ink/40">{r.href}</span>
              </button>
              <button
                type="button"
                onClick={() => toggleVisible.mutate(r)}
                className={`rounded-sm px-2 py-0.5 font-sans text-[0.7rem] uppercase tracking-[0.12em] ${
                  r.is_visible ? 'bg-emerald-600/10 text-emerald-700' : 'bg-ink/10 text-ink/50'
                }`}
              >
                {r.is_visible ? 'Visible' : 'Hidden'}
              </button>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate(r)}
                className="font-sans text-xs text-oxblood hover:underline disabled:opacity-60"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}

      <Drawer
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit item' : 'New item'}
      >
        {editing && (
          <MenuItemForm
            item={editing}
            onDone={() => {
              setEditing(null);
              invalidate();
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

function MenuItemForm({ item, onDone }: { item: Partial<NavMenuItem>; onDone: () => void }) {
  const [label, setLabel] = useState(item.label ?? '');
  const [href, setHref] = useState(item.href ?? '');
  const [isVisible, setIsVisible] = useState(item.is_visible ?? true);

  const save = useMutation({
    mutationFn: async () => {
      const trimmedLabel = label.trim();
      const trimmedHref = href.trim();
      if (!trimmedLabel) throw new Error('Label is required.');
      if (!trimmedHref) throw new Error('Link is required.');
      // A typo here ships a broken nav link to prod browse instantly.
      if (!trimmedHref.startsWith('/') && !trimmedHref.startsWith('https://')) {
        throw new Error('Link must start with "/" (in-app) or "https://" (external).');
      }
      await catalogMutations.saveMenuItem({
        id: item.id,
        label: trimmedLabel,
        href: trimmedHref,
        is_visible: isVisible,
        sort_order: item.sort_order ?? 0,
      });
    },
    onSuccess: () => {
      toast.success('Menu item saved.');
      onDone();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed.'),
  });

  return (
    <div className="space-y-4">
      <Field label="Label">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </Field>
      <Field label="Link (href)">
        <input
          value={href}
          onChange={(e) => setHref(e.target.value)}
          placeholder="/browse or https://"
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </Field>
      <label className="flex items-center gap-2 font-sans text-sm text-ink/80">
        <input
          type="checkbox"
          checked={isVisible}
          onChange={(e) => setIsVisible(e.target.checked)}
          className="accent-oxblood"
        />
        Visible in the menu bar
      </label>
      <button
        type="button"
        disabled={save.isPending}
        onClick={() => save.mutate()}
        className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
      >
        Save item
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
        {label}
      </label>
      {children}
    </div>
  );
}
